import { useState, useMemo, useEffect } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Plus, List, Columns3, CalendarDays, GanttChartSquare, MessageCircle, Download, MoreVertical, Copy, Trash2, LayoutTemplate, Printer } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { applyTaskFilters, PROJECT_STATUSES, PRIORITIES, TASK_RECURRENCES } from "@/lib/taskConstants";
import { FilterBar } from "@/components/project/FilterBar";
import { ListView } from "@/components/project/ListView";
import { KanbanView } from "@/components/project/KanbanView";
import { CalendarView } from "@/components/project/CalendarView";
import { GanttView } from "@/components/project/GanttView";
import { GuidedTour } from "@/components/GuidedTour";
import { useAuth } from "@/context/AuthContext";
import { TaskDetailSheet } from "@/components/project/TaskDetailSheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const VIEWS = [
  { value: "list", label: "Lista", icon: List },
  { value: "kanban", label: "Kanban", icon: Columns3 },
  { value: "calendar", label: "Calendario", icon: CalendarDays },
  { value: "gantt", label: "Gantt", icon: GanttChartSquare },
];

const NONE = "__none__";

const NewTaskDialog = ({ open, onOpenChange, projectId, members }) => {
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [assignee, setAssignee] = useState(NONE);
  const [priority, setPriority] = useState("medium");
  const [startDate, setStartDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [reward, setReward] = useState("");
  const [recurrence, setRecurrence] = useState("none");
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!title.trim()) return toast.error("El título es obligatorio");
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/tasks`, {
        title, priority,
        assignee_id: assignee === NONE ? null : assignee,
        start_date: startDate || null, due_date: dueDate || null,
        reward_amount: reward ? parseFloat(reward) : null,
        recurrence,
      });
      qc.invalidateQueries({ queryKey: ["tasks", projectId] });
      toast.success("Tarea creada");
      onOpenChange(false);
      setTitle(""); setStartDate(""); setDueDate(""); setAssignee(NONE); setPriority("medium"); setReward("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-task-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Nueva tarea</DialogTitle>
          <DialogDescription>Se añadirá al proyecto actual.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Título</Label>
            <Input data-testid="new-task-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && create()} placeholder="¿Qué hay que hacer?" maxLength={300} autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Responsable</Label>
              <Select value={assignee} onValueChange={setAssignee}>
                <SelectTrigger data-testid="new-task-assignee-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Sin responsable</SelectItem>
                  {members.map((m) => <SelectItem key={m.profile_id} value={m.profile_id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Prioridad</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="new-task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Fecha inicio</Label>
              <Input type="date" data-testid="new-task-start-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Fecha límite</Label>
              <Input type="date" data-testid="new-task-due-input" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Recompensa (€) <span className="text-muted-foreground text-xs">opcional</span></Label>
              <Input type="number" min="0" step="0.5" placeholder="0" data-testid="new-task-reward-input"
                value={reward} onChange={(e) => setReward(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Repetición</Label>
              <Select value={recurrence} onValueChange={setRecurrence}>
                <SelectTrigger data-testid="new-task-recurrence-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TASK_RECURRENCES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button className="w-full" data-testid="new-task-submit-btn" onClick={create} disabled={saving}>
            {saving ? "Creando..." : "Crear tarea"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function ProjectPage() {
  const { id: workspaceId, projectId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = params.get("view") || "list";
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ q: "", assignee: "all", status: "all", priority: "all", overdue: false });
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    const t = params.get("task");
    if (t) setSelectedTaskId(t);
  }, [params]);

  const openProjectChat = async () => {
    const { data } = await api.get("/conversations/for-entity", {
      params: { workspace_id: workspaceId, entity_type: "project", entity_id: projectId },
    });
    navigate(`/space/${workspaceId}/chat?c=${data.id}`);
  };

  const { data: project, isLoading, error } = useQuery({
    queryKey: ["project", projectId],
    queryFn: async () => (await api.get(`/projects/${projectId}`)).data,
    retry: false,  });
  const { data: allTasks = [] } = useQuery({
    queryKey: ["tasks", projectId],
    queryFn: async () => (await api.get(`/projects/${projectId}/tasks`)).data,
    enabled: !!project,
  });
  const { data: members = [] } = useQuery({
    queryKey: ["members", workspaceId],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}/members`)).data,
  });
  const { data: taskImages = [] } = useQuery({
    queryKey: ["task-images", workspaceId],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}/files?entity_type=task&category=image`)).data,
    enabled: view === "kanban",
  });
  const imagesByTask = useMemo(() => {
    const map = {};
    for (const f of taskImages) {
      if (f.entity_id && !map[f.entity_id]) map[f.entity_id] = f;
    }
    return map;
  }, [taskImages]);

  const updateTask = useMutation({
    mutationFn: async ({ taskId, payload }) => (await api.put(`/tasks/${taskId}`, payload)).data,
    onMutate: async ({ taskId, payload }) => {
      await qc.cancelQueries({ queryKey: ["tasks", projectId] });
      const prev = qc.getQueryData(["tasks", projectId]);
      qc.setQueryData(["tasks", projectId], (old = []) =>
        old.map((t) => (t.id === taskId ? { ...t, ...payload } : t)));
      return { prev };
    },
    onError: (e, _v, ctx) => {
      qc.setQueryData(["tasks", projectId], ctx.prev);
      toast.error(apiError(e));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["tasks", projectId] }),
  });

  const parentTasks = useMemo(() => allTasks.filter((t) => !t.parent_task_id && t.status !== "archived"), [allTasks]);
  const subtasksByParent = useMemo(() => {
    const map = {};
    for (const t of allTasks) {
      if (t.parent_task_id) (map[t.parent_task_id] = map[t.parent_task_id] || []).push(t);
    }
    return map;
  }, [allTasks]);
  const filteredTasks = useMemo(() => applyTaskFilters(parentTasks, filters), [parentTasks, filters]);
  const selectedTask = allTasks.find((t) => t.id === selectedTaskId) || null;

  const handleToggleDone = (task) =>
    updateTask.mutate({ taskId: task.id, payload: { status: task.status === "done" ? "todo" : "done" } });

  const handleKanbanMove = (taskId, status, order) =>
    updateTask.mutate({ taskId, payload: { status, order } });

  const handleGanttDates = (taskId, dates) =>
    updateTask.mutate({ taskId, payload: dates });

  const handleFieldUpdate = (taskId, patch) =>
    updateTask.mutate({ taskId, payload: patch });

  const duplicateProject = async () => {
    try {
      const { data } = await api.post(`/projects/${projectId}/duplicate`);
      qc.invalidateQueries({ queryKey: ["projects", workspaceId] });
      toast.success(`Proyecto duplicado: ${data.name}`);
      navigate(`/space/${workspaceId}/project/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveAsTemplate = async () => {
    try {
      const { data } = await api.post(`/projects/${projectId}/save-as-template`, {});
      qc.invalidateQueries({ queryKey: ["templates", workspaceId] });
      toast.success(`Plantilla "${data.name}" guardada en la galería del Espacio`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteProject = async () => {
    try {
      await api.delete(`/projects/${projectId}`);
      qc.invalidateQueries({ queryKey: ["projects", workspaceId] });
      toast.success("Proyecto eliminado");
      navigate(`/space/${workspaceId}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const handleQuickCreate = async (title, status) => {
    try {
      await api.post(`/projects/${projectId}/tasks`, { title, status });
      qc.invalidateQueries({ queryKey: ["tasks", projectId] });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const updateProjectStatus = async (status) => {
    try {
      await api.put(`/projects/${projectId}`, { status });
      qc.invalidateQueries({ queryKey: ["project", projectId] });
      qc.invalidateQueries({ queryKey: ["projects", workspaceId] });
      toast.success("Proyecto actualizado");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center space-y-3">
        <p className="font-heading font-semibold text-lg">{apiError(error)}</p>
        <Button asChild variant="outline"><Link to={`/space/${workspaceId}`}>Volver al Espacio</Link></Button>
      </div>
    );
  }

  if (isLoading || !project) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 space-y-4">
        <Skeleton className="h-16 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="project-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button asChild variant="ghost" size="sm" className="shrink-0" data-testid="project-back-btn">
          <Link to={`/space/${workspaceId}`}><ArrowLeft className="h-4 w-4" /></Link>
        </Button>
        <div className="h-9 w-9 rounded-lg flex items-center justify-center text-lg shrink-0"
          style={{ backgroundColor: `${project.color}1f`, border: `1px solid ${project.color}40` }}>
          {project.icon}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="font-heading text-xl font-bold tracking-tight truncate" data-testid="project-name">{project.name}</h1>
          {project.description && <p className="text-xs text-muted-foreground truncate">{project.description}</p>}
        </div>
        <Select value={project.status} onValueChange={updateProjectStatus}>
          <SelectTrigger className="w-32 h-8 text-xs" data-testid="project-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PROJECT_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {project.property_id && (
          <Button asChild size="sm" variant="outline" data-testid="project-property-link">
            <Link to={`/space/${workspaceId}/realestate/${project.property_id}`}>🏠 Ficha inmueble</Link>
          </Button>
        )}
        <Button size="sm" variant="outline" data-testid="project-export-btn" title="Exportar CSV"
          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/tasks-export`, "_blank")}>
          <Download className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="outline" data-testid="project-print-pdf-btn" title="Imprimir en PDF (Premium)"
          onClick={async () => {
            try {
              const { data } = await api.get("/subscription/me");
              if (data.plan !== "premium") {
                toast.info("Imprimir en PDF es una función Premium. Actívala con la prueba gratis de 14 días.");
                navigate("/subscription");
                return;
              }
              window.print();
            } catch {
              window.print();
            }
          }}>
          <Printer className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="outline" data-testid="project-chat-btn" onClick={openProjectChat}>
          <MessageCircle className="h-4 w-4" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline" data-testid="project-menu-btn">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem data-testid="project-duplicate-btn" onClick={duplicateProject}>
              <Copy className="h-4 w-4 mr-2" /> Duplicar proyecto
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="project-save-template-btn" onClick={saveAsTemplate}>
              <LayoutTemplate className="h-4 w-4 mr-2" /> Guardar como plantilla
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="project-delete-btn" className="text-destructive focus:text-destructive"
              onClick={() => setDeleteOpen(true)}>
              <Trash2 className="h-4 w-4 mr-2" /> Eliminar proyecto
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar este proyecto?</AlertDialogTitle>
              <AlertDialogDescription>
                Se eliminarán el proyecto "{project.name}" y todas sus tareas. Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction data-testid="project-delete-confirm-btn" onClick={deleteProject}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <Button size="sm" data-testid="project-new-task-btn" onClick={() => setNewTaskOpen(true)}>
          <Plus className="h-4 w-4 mr-1" /> Nueva tarea
        </Button>
      </div>

      <div className="flex rounded-lg border overflow-hidden w-fit">
        {VIEWS.map((v) => (
          <button key={v.value} onClick={() => setParams({ view: v.value })}
            data-testid={`view-tab-${v.value}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
              view === v.value ? "bg-primary text-primary-foreground" : "hover:bg-secondary"
            }`}>
            <v.icon className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{v.label}</span>
          </button>
        ))}
      </div>

      <FilterBar filters={filters} setFilters={setFilters} members={members} />

      {view === "list" && (
        <ListView tasks={filteredTasks} subtasksByParent={subtasksByParent} members={members}
          onSelect={setSelectedTaskId} onToggleDone={handleToggleDone} />
      )}
      {view === "kanban" && (
        <KanbanView tasks={filteredTasks} subtasksByParent={subtasksByParent} members={members}
          onUpdateField={handleFieldUpdate}
          imagesByTask={imagesByTask}
          onSelect={setSelectedTaskId} onMove={handleKanbanMove} onQuickCreate={handleQuickCreate} />
      )}
      {view === "calendar" && (
        <CalendarView tasks={filteredTasks} onSelect={setSelectedTaskId} />
      )}
      {view === "gantt" && (
        <GanttView tasks={filteredTasks} subtasksByParent={subtasksByParent} onSelect={setSelectedTaskId} onUpdateDates={handleGanttDates} />
      )}

      <TaskDetailSheet task={selectedTask} subtasks={selectedTask ? subtasksByParent[selectedTask.id] || [] : []}
        members={members} projectId={projectId} allTasks={allTasks.filter((t) => !t.parent_task_id)}
        onClose={() => setSelectedTaskId(null)} onSelectTask={setSelectedTaskId} />
      <NewTaskDialog open={newTaskOpen} onOpenChange={setNewTaskOpen} projectId={projectId} members={members} />

      {user?.tour_done && (
        <GuidedTour
          flag="gantt_tip_done"
          testPrefix="gantt-tip"
          steps={[
            { target: '[data-testid="view-tab-gantt"]', emoji: "📊", title: "Prueba la vista Gantt", text: "Visualiza las tareas en el tiempo, arrastra sus barras para cambiar fechas y detecta cuellos de botella de un vistazo." },
          ]}
        />
      )}
    </div>
  );
}
